// messagesSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
    getWaChatsThunk,
    updateMessagesCountThunk,
    submitNewMessageThunk,
    getChatContacts,
    sendWaConvoTemplateThunk,
    getContactFilters,
} from "./messagesThunk";

const initialState = {
    status: "idle",
    status1: "idle",
    status2: "idle",
    status3: "idle",
    error: null,
    success: "",
    selectedUser: null,
    waMessageChats: [],
    typeMessage: "",
    isReplyMessage: null,
    addTagModal: false,
    waMessageContacts: [],
    waContactUserFilterArr: [],
    paginationDetails: [],


    hasMore: true,
    loading: false,
    page: 1,
    totalPages: 1,


    chatPage: 1,
    hasMoreChats: true,
    loadingChats: false,
    totalChatPages: 1,
};

const messagesSlice = createSlice({
    name: "messages",
    initialState,
    reducers: {
        resetMessagesState: (state) => {
            state.status = "idle";
            state.error = null;
            state.success = "";
            state.hasMore = true;
            state.loading = false;
            state.hasMoreChats = true;
            state.loadingChats = false;

        },
        setSelectUser: (state, action) => {
            state.selectedUser = action.payload;
        },
        setMessage: (state, action) => {
            state.typeMessage = action.payload;
        },
        setReplyMessage: (state, action) => {
            state.isReplyMessage = action.payload;
        },
        setOpenTagModal: (state, action) => {
            state.addTagModal = action.payload;
        },
        setLeadWaDetails: (state, action) => {
            state.waMessageContacts = action.payload;
        },
        // 🔹 new reducer to clear chats
        clearWaMessageChats: (state) => {
            state.waMessageChats = [];
            state.selectedUser = null;
        },

        clearChats: (state) => {
            state.chatPage = 1;
            state.hasMoreChats = true;
            state.loadingChats = false;
        },
        clearLoader: (state) => {
            state.hasMore = 1;
            state.loading = false;
            state.page = false;
        },

        // socket-driven reducers
        updateSentMessage: (state, action) => {
            const val = action.payload;
            const contactId = val[0]?.wa_contact_id;
            const newMessages = val[0]?.messages?.["TODAY"] || [];

            if (!contactId || newMessages.length === 0) return;

            state.waMessageChats = state.waMessageChats.map(chat => {
                if (chat.wa_contact_id !== contactId) return chat;

                const todayMessages = chat.messages?.["TODAY"] || [];
                const mergedMessages = [
                    ...todayMessages.filter(
                        msg => !newMessages.some(nm => nm.id === msg.id)
                    ),
                    ...newMessages,
                ];

                return {
                    ...chat,
                    messages: {
                        ...chat.messages,
                        TODAY: mergedMessages,
                    },
                };
            });
        },
        // ✅ Handle new incoming message (from socket)
        fetchMessageResponse: (state, action) => {
            const { val, data } = action.payload;
            if (val === "new") {
                state.waMessageChats = [data, ...state.waMessageChats];
            } else {
                const messageData = data.messages?.TODAY?.[0];
                state.waMessageChats = state.waMessageChats.map((chat) => {
                    if (chat.wa_contact_id === state.selectedUser) {
                        const todayMessages = chat.messages?.["TODAY"] || [];
                        return {
                            ...chat,
                            is_24hour_timeline: data.is_24hour_timeline,
                            messages: {
                                ...chat.messages,
                                TODAY: [...todayMessages, messageData],
                            },
                            unread: (chat.unread || 0) + 1,
                        };
                    }
                    return chat;
                });
            }
        },

        // ✅ Update message delivery/read status
        statusUpdate: (state, action) => {
            const val = action.payload;
            state.waMessageChats = state.waMessageChats.map((chat) => {
                if (chat.wa_contact_id === state.selectedUser) {
                    const dateMessages = chat.messages[val.date] || [];

                    const updatedMessages = dateMessages.map((message) =>
                        message.id === val.chat_id
                            ? { ...message, status: val.status }
                            : message
                    );

                    return {
                        ...chat,
                        messages: { ...chat.messages, [val.date]: updatedMessages },
                    };
                }
                return chat;
            });
        },

        unReadCountUpdate: (state, action) => {
            const { wa_contact_id, unread, last_message_time, last_message } = action.payload;

            // Update the specific contact
            state.waMessageContacts = state.waMessageContacts.map((contact) => {
                if (contact.wa_contact_id === wa_contact_id) {
                    return {
                        ...contact,
                        last_message: last_message,
                        last_message_time: last_message_time,
                        unread: String(unread),
                    };
                }
                return contact;
            });

            // Sort contacts so latest message comes first
            state.waMessageContacts.sort((a, b) => {
                return b.last_message_time - a.last_message_time; // timestamps are numeric strings
            });
        }
    },
    extraReducers: (builder) => {
        builder
            // WA CHATS
            .addCase(getWaChatsThunk.pending, (state) => {
                // state.status1 = "loading";
                state.error = null;
                state.loadingChats = true;
            })
            .addCase(getWaChatsThunk.fulfilled, (state, action) => {
                const updatedData = Array.isArray(action.payload)
                    ? action.payload
                    : action.payload?.updatedData;

                if (!updatedData || updatedData.length === 0) {
                    state.loadingChats = false;
                    return;
                }

                const firstItem = updatedData[0];
                const {
                    is_24hour_timeline = false,
                    total_count = 0,
                    total_pages = 1,
                    wa_contact_id = null,
                    current_page = 1,
                    limit = 20
                } = firstItem;

                const mergeMessages = (existing = {}, newPages = []) => {
                    const merged = { ...existing };

                    newPages.forEach(page => {
                        Object.entries(page.messages || {}).forEach(([date, msgs]) => {
                            if (!merged[date]) merged[date] = [];
                            merged[date].push(...msgs);
                        });
                    });

                    // Sort messages within each date (time ascending)
                    Object.keys(merged).forEach(date => {
                        merged[date].sort((a, b) => (a.time > b.time ? 1 : -1));
                    });

                    // ✅ Sort all date keys so OLDEST first, LATEST last
                    const sortedKeys = Object.keys(merged).sort((a, b) => {
                        const toTime = (key) => {
                            if (key === "TODAY") return new Date().getTime();
                            if (key === "YESTERDAY") {
                                const d = new Date();
                                d.setDate(d.getDate() - 1);
                                return d.getTime();
                            }
                            const [dd, mm, yyyy] = key.split("/");
                            return new Date(`${yyyy}-${mm}-${dd}`).getTime();
                        };
                        return toTime(a) - toTime(b); // oldest first ✅
                    });

                    // ✅ Rebuild object in correct chronological order
                    const ordered = {};
                    sortedKeys.forEach(k => {
                        ordered[k] = merged[k];
                    });

                    return ordered;
                };


                // if (current_page === 1 || state.waMessageChats.length === 0) {
                //     state.waMessageChats = [
                //         {
                //             messages: mergeMessages({}, updatedData),
                //             is_24hour_timeline,
                //             total_count,
                //             total_pages,
                //             wa_contact_id,
                //             current_page,
                //             limit
                //         }
                //     ];
                // } else {
                //     const existing = state.waMessageChats[0];
                //     state.waMessageChats = [
                //         {
                //             ...existing,
                //             messages: mergeMessages(existing.messages, updatedData),
                //             total_count,
                //             total_pages,
                //             current_page,
                //             is_24hour_timeline,
                //         }
                //     ];
                // }

                const existing = state.waMessageChats[0];
                const isNewContact = !existing || existing.wa_contact_id !== wa_contact_id;

                if (isNewContact) {
                    state.waMessageChats = [
                        {
                            messages: mergeMessages({}, updatedData),
                            is_24hour_timeline,
                            total_count,
                            total_pages,
                            wa_contact_id,
                            current_page,
                            limit
                        }
                    ];
                } else {
                    state.waMessageChats = [
                        {
                            ...existing,
                            messages: mergeMessages(existing.messages, updatedData),
                            total_count,
                            total_pages,
                            current_page,
                            is_24hour_timeline
                        }
                    ];
                }

                state.success = true;
                state.error = null;
                state.loadingChats = false;
                state.chatPage = current_page;
                state.totalChatPages = total_pages;
                state.hasMoreChats = current_page < total_pages;
            }).addCase(getWaChatsThunk.rejected, (state, action) => {
                // state.status1 = "failed";
                state.error = action.payload || "Fetching chats failed";
                state.success = "";
                state.loadingChats = false;
            })









            // WA USER CONTACTS
            .addCase(getChatContacts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getChatContacts.fulfilled, (state, action) => {
                state.loading = false;
                const { data, pagination } = action.payload;

                // append new contacts
                if (pagination.page === 1) {
                    // Reset contacts for first page (e.g., on filter change)
                    state.waMessageContacts = data;
                } else {
                    // Append next page data
                    const existingIds = new Set(state.waMessageContacts.map(c => c.wa_contact_id));
                    const newData = data.filter(c => !existingIds.has(c.wa_contact_id));
                    state.waMessageContacts = [...state.waMessageContacts, ...newData];
                }

                // update pagination info
                state.page = pagination.page;
                state.totalPages = pagination.totalPages;
                state.hasMore = pagination.page < pagination.totalPages;
            })
            .addCase(getChatContacts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || "Fetching chats failed";
                state.hasMore = false;
            })


            // UN READ COUNT
            .addCase(updateMessagesCountThunk.fulfilled, (state, action) => {
                const { wa_contact_id } = action.payload;

                state.waMessageContacts = state.waMessageContacts.map((user) => {
                    if (user.wa_contact_id === wa_contact_id) {
                        return {
                            ...user,
                            unread: 0
                        };
                    }
                    return user;
                });
            })
            .addCase(updateMessagesCountThunk.rejected, (state, action) => {
                state.error = action.error.message;
            })


            // SEND MESSAGE 
            .addCase(submitNewMessageThunk.rejected, (state, action) => {
                state.error = action.payload || "Message send failed";
            })



            // send Template
            .addCase(sendWaConvoTemplateThunk.pending, (state) => {
                state.status2 = "loading";
                state.error = null;
            })
            .addCase(sendWaConvoTemplateThunk.fulfilled, (state, action) => {
                state.status2 = "succeeded";
                state.success = action.payload.success;
                state.error = null;
            })
            .addCase(sendWaConvoTemplateThunk.rejected, (state, action) => {
                state.status2 = "failed";
                state.error = action.payload || "Login failed";
                state.success = "";
            })

            // CONTACT FILTER
            .addCase(getContactFilters.pending, (state) => {
                state.status3 = "loading";
                state.error = null;
            })
            .addCase(getContactFilters.fulfilled, (state, action) => {
                state.status3 = "succeeded";
                state.success = action.payload.success;
                state.error = null;
                state.waContactUserFilterArr = action.payload;
            })
            .addCase(getContactFilters.rejected, (state, action) => {
                state.status3 = "failed";
                state.error = action.payload || "Fetching chats failed";
                state.success = "";
            })
    },
});

export const {
    resetMessagesState,
    setSelectUser,
    setMessage,
    setReplyMessage,
    updateSentMessage,
    fetchMessageResponse,
    statusUpdate,
    unReadCountUpdate,
    setOpenTagModal,
    clearWaMessageChats,
    setLeadWaDetails,
    clearChats,
    clearLoader,
} = messagesSlice.actions;

export default messagesSlice.reducer;
